import { useEffect, useMemo, useState } from "react";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import { i18nRoot, maxChapterCount } from "../constants";
import type { MediaChapterFormData } from "../types";
import { formatTime } from "../utils/timeHelpers";

interface AddChapterButtonProps {
  chapters: MediaChapterFormData[];
  className?: string;
  currentTime: number;
  duration: number;
  onChapterAdd: () => void;
}

export const AddChapterButton = ({
  className,
  chapters,
  currentTime,
  duration,
  onChapterAdd,
}: AddChapterButtonProps) => {
  const [wasChapterJustAdded, setWasChapterJustAdded] =
    useState<boolean>(false);
  const chapterCount = chapters.length;
  const isChapterLimitReached = chapterCount >= maxChapterCount;
  const isVideoEndReached = Math.floor(currentTime) === Math.floor(duration);
  const newChapterStartTime = chapterCount === 0 ? 0.0 : currentTime;

  const isDuplicateTimestamp = useMemo(
    () =>
      chapters.some(
        chapter =>
          Math.floor(chapter.start_time) === Math.floor(newChapterStartTime),
      ),
    [chapters, newChapterStartTime],
  );

  const addButtonTooltipText = useMemo(() => {
    if (isChapterLimitReached) {
      return t(`${i18nRoot}.chapters.chapter_limit_reached_tooltip`, {
        max_chapters_count: maxChapterCount,
      });
    }
    if (isDuplicateTimestamp) {
      return t(`${i18nRoot}.chapters.duplicate_timestamp_tooltip`, {
        timestamp: formatTime(currentTime),
      });
    }
    if (isVideoEndReached) {
      return t(`${i18nRoot}.chapters.video_end_reached_tooltip`);
    }
    return "";
  }, [
    isChapterLimitReached,
    isDuplicateTimestamp,
    isVideoEndReached,
    currentTime,
  ]);

  // A workaround to avoid showing the tooltip while the player is being seeked
  // to the next suggested chapter time when the chapter has been just added
  useEffect(() => {
    const timeout = setTimeout(() => setWasChapterJustAdded(false), 1000);

    return () => clearTimeout(timeout);
  }, [wasChapterJustAdded]);

  const isDisabled =
    isChapterLimitReached || isDuplicateTimestamp || isVideoEndReached;
  const isTooltipEnabled = isDisabled && !wasChapterJustAdded;

  const handleChapterAdd = () => {
    setWasChapterJustAdded(true);
    onChapterAdd();
  };

  return (
    <TippyV2 content={addButtonTooltipText} enabled={isTooltipEnabled}>
      <Button
        className={classNames("flex gap-1", className)}
        type="button"
        variant="secondary"
        onClick={handleChapterAdd}
        disabled={isDisabled}
      >
        <Icon type="plus-v2" size={16} className="!size-4" />
        <Typography.LabelXs weight="medium">
          {t(`${i18nRoot}.chapters.add_at`, {
            timestamp: formatTime(currentTime),
          })}
        </Typography.LabelXs>
      </Button>
    </TippyV2>
  );
};
